import React, { useState, useCallback, useMemo } from "react";
import {
  Button,
  Flex,
  FileObjectType,
  FileUpload,
  Link,
  Text,
  FileIcon,
  Banner,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { formatDate } from "../../../utils/date_formatter";
import { DefaultModal } from "../../common/DefaultModal";
import { isDocumentExpired } from "../utils";
import {
  uploaderInfoContainer,
  uploaderInfoIcon,
  uploaderInfoText,
  deleteIcon,
  deleteIconContainer,
} from "./style_vanilla.css";
import { DocumentUploaderProps } from "./types";
import { hasUnsavedDocumentChanges } from "./utils";

const SUPPORTED_FORMATS = ["image/*", ".pdf", ".doc", ".docx", ".xlsx", ".xls", ".csv", ".zip"];

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  document,
  backNavText,
  isSubmitting,
  onNavigateBack,
  onSave,
}) => {
  const { file, id, uploaderInfo, lastUploadedDate, expiryDate } = document;

  const isExpired = useMemo(() => {
    return isDocumentExpired(expiryDate);
  }, [expiryDate]);

  const initialFileWithError = useMemo(() => {
    if (!file) return null;
    if (isExpired) {
      return {
        ...file,
        error: "Document has expired",
      };
    }
    return file;
  }, [file, isExpired]);

  const [currentFile, setCurrentFile] = useState<FileObjectType | null>(initialFileWithError);
  const [currentUploaderInfo, setCurrentUploaderInfo] = useState<string | null>(
    uploaderInfo || null,
  );

  const hasReplacedFile = currentFile !== initialFileWithError && currentFile !== null;
  const showExpiredState = isExpired && !hasReplacedFile;

  const [showBackWarningModal, setShowBackWarningModal] = useState(false);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);

  const handleFileSelected = useCallback(([selectedFile]: FileObjectType[]) => {
    if (!selectedFile) return;
    setCurrentFile(selectedFile);
  }, []);

  const handleFileDeleted = useCallback(() => {
    setCurrentFile(null);
  }, []);

  const hasUnsavedChanges = useMemo(
    () =>
      hasUnsavedDocumentChanges(initialFileWithError, currentFile) ||
      (uploaderInfo != null && currentUploaderInfo === null),
    [initialFileWithError, currentFile, uploaderInfo, currentUploaderInfo],
  );

  const handleSave = useCallback(() => {
    if (hasUnsavedChanges) {
      document.file = currentFile;
      document.uploaderInfo = currentUploaderInfo;
      onSave(document);
    }
  }, [document, hasUnsavedChanges, currentFile, currentUploaderInfo, onSave]);

  const handleDeleteGuestUpload = useCallback(() => {
    setCurrentUploaderInfo(null);
    handleFileDeleted();
    setShowDeleteWarningModal(false);
  }, [handleFileDeleted]);

  const handleNavigateBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowBackWarningModal(true);
    } else {
      onNavigateBack();
    }
  }, [hasUnsavedChanges, onNavigateBack]);

  const confirmNavigateBack = useCallback(() => {
    setShowBackWarningModal(false);
    onNavigateBack();
  }, [onNavigateBack]);

  const isSaveDisabled = !hasUnsavedChanges || isSubmitting;

  const showGuestInfo = !!(currentUploaderInfo && id);
  const bannerDescription = useMemo(() => {
    const baseMessage = "Upload the latest document to meet regulatory requirements.";
    if (lastUploadedDate) {
      return `${baseMessage} The expired document was last uploaded on ${formatDate(lastUploadedDate)}.`;
    }
    return baseMessage;
  }, [lastUploadedDate]);

  return (
    <>
      <Flex flexDirection="column" gap={Sizes.MD}>
        <Link
          to=""
          icon="arrow-left"
          iconPosition="left"
          onClick={handleNavigateBack}
          variant="muted"
          align="left"
        >
          {backNavText}
        </Link>

        <Text variant="h3">{document.title}</Text>

        <Text variant="regular-md">{document.description}</Text>

        {showExpiredState && (
          <Banner
            variant="warning"
            title={`${document.title} has expired`}
            description={bannerDescription}
          />
        )}

        <FileUpload
          key={currentFile?.uuid}
          acceptedFileTypes={SUPPORTED_FORMATS}
          canAllowMultiple={false}
          alwaysShowGuidance
          isFullWidth
          disableFileDeletion={showExpiredState}
          guidanceMessage={document.example}
          onDelete={handleFileDeleted}
          onFileSelected={handleFileSelected}
          initialFiles={currentFile ? [currentFile] : []}
          data-testid="file-attach-button"
          isDisabled={showGuestInfo || isSubmitting}
        />

        {showGuestInfo && (
          <Flex className={uploaderInfoContainer}>
            <Flex className={uploaderInfoIcon}>
              <FileIcon extension="" size="x-large" />
            </Flex>
            <Text variant="regular-md" className={uploaderInfoText}>
              Uploaded by {currentUploaderInfo}
            </Text>
            <Flex className={deleteIconContainer}>
              <Icon
                data-testid={`delete-guest-upload-icon`}
                type="trash"
                size="large"
                className={deleteIcon}
                color={Colors.Neutral900}
                onClick={() => setShowDeleteWarningModal(true)}
              />
            </Flex>
          </Flex>
        )}
        <Flex flexDirection="row" gap={Sizes.SM} justifyContent="flex-end">
          <Button variant="neutral" onClick={handleNavigateBack} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            isLoading={isSubmitting}
            disabled={isSaveDisabled}
            data-testid="save-document-button"
          >
            Save document
          </Button>
        </Flex>
      </Flex>

      <DefaultModal
        show={showBackWarningModal}
        title="Warning"
        body="You have unsaved changes. Are you sure you want to go back without submitting?"
        negBtnText="Cancel"
        posBtnText="Back"
        posBtnVariant="danger"
        onClose={() => setShowBackWarningModal(false)}
        onConfirm={confirmNavigateBack}
      />

      <DefaultModal
        show={showDeleteWarningModal}
        modalSize="small"
        title="Warning"
        body={
          <Flex flexDirection="column" gap={Sizes.MD}>
            <Text>
              This document was uploaded on {formatDate(lastUploadedDate)} by{" "}
              {currentUploaderInfo}.
            </Text>
            <Text>Are you sure you want to delete it?</Text>
          </Flex>
        }
        negBtnText="Cancel"
        posBtnText="Delete"
        posBtnVariant="danger"
        onClose={() => setShowDeleteWarningModal(false)}
        onConfirm={handleDeleteGuestUpload}
      />
    </>
  );
};
